/**
 * PropertyDealAnalyzer.jsx — Phase C polished bilingual production UI.
 *
 * Route: /property-deal-analyzer  (still noindex until Phase D integration).
 *
 * User-approved directives baked in:
 *   1. Scenario growth = free-form user input, accepts negative values.
 *      Clear note explaining values can be negative or positive.
 *   2. Break-even rent = headline metric card.
 *   3. Cash mode blacks out mortgage fields; Mortgage mode validates
 *      that DP%, interest and term are > 0 and shows red errors otherwise.
 *   4. FAQ is professional Arabic translation (not literal).
 *
 * Design: matches site design language — propertic-navy hero, propertic-gold
 * accents, El Messiri / Noto Sans Arabic for AR, framer-motion for reveals.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import {
  Calculator, ChevronDown, Info, AlertCircle, TrendingUp, ArrowRight, Sparkles,
} from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useCurrency } from "@/context/CurrencyContext";
import { analyzeDeal } from "@/lib/dealAnalyzer";
import ConsultationForm from "@/components/forms/ConsultationForm";
import { arDigits, arCount, normalizeDigits, fixArThousands } from "@/lib/utils";

const AED_PER_USD_PEG = 3.6725;

// ---------- copy (EN + AR) ----------
const COPY = {
  en: {
    hero_kicker: "Property Deal Analyzer",
    hero_title: "See the real economics behind the price.",
    hero_sub: "Enter your own numbers to calculate acquisition cost, yields, cash flow and scenario outcomes. Neutral math. Not a Propertic forecast, valuation or investment recommendation.",
    property_title: "Property & operating",
    field_price: "Purchase price",
    field_size: "Size (sqft)",
    field_size_hint: "optional, used for per sqft display",
    field_rent: "Annual rent",
    field_sc: "Annual service charges",
    field_mgmt: "Annual management cost",
    field_mgmt_hint: "e.g. short let, holiday home operator",
    field_other_opex: "Other annual expenses",
    field_other_opex_hint: "e.g. daily rent utilities, insurance, chiller",
    acq_title: "Acquisition costs",
    field_dld: "Property transfer / registration fee %",
    field_dld_hint: "Varies by emirate or country. Enter the applicable rate.",
    field_dld_aed_note: "Dubai reference: transfer fees can vary by transaction. Verify the applicable DLD fee before relying on the calculation.",
    field_broker: "Broker fee %",
    field_broker_hint: "Typically about 2% for ready, 0% for off plan",
    field_other_purchase: "Other purchase costs",
    field_other_purchase_hint: "developer admin charges, NOC, valuation, insurance",
    fin_title: "Financing",
    mode_cash: "Cash",
    mode_mortgage: "Mortgage",
    field_dp_pct: "Down payment %",
    field_rate: "Interest rate %",
    field_term: "Term (years)",
    field_mortgage_costs: "Mortgage related costs",
    field_mortgage_costs_hint: "arrangement, valuation, one time insurance",
    err_dp: "Enter a down payment % greater than 0",
    err_rate: "Enter an interest rate",
    err_term: "Enter a term in years",
    target_title: "Target yield",
    field_target_yield: "Target net yield %",
    field_target_yield_hint: "used by the required rent calculator below",
    scenario_title: "Optional, model your own growth assumptions",
    scenario_note: "Off by default. Any growth values you enter are YOUR assumptions, not a Propertic forecast or valuation. Leaving this off keeps property value, rent and operating costs constant; the loan balance and equity still change with repayment, and mortgage payments stop when the loan term ends. Values can be negative or positive (for example 3% for a downturn, 5% for a bull case).",
    field_appreciation: "Property value change % / yr",
    field_rent_growth: "Rent growth % / yr",
    field_expense_growth: "Expense growth % / yr",
    field_vacancy: "Vacancy % / yr",
    field_holding: "Holding period (years)",
    field_growth_hint: "can be positive or negative",
    metrics_title: "Deal metrics",
    m_break_even: "Cash flow break even rent",
    m_break_even_sub: "The smallest yearly rent you can charge and still cover every running cost plus any mortgage payment. Below this figure the property runs at a loss.",
    m_net_yield: "Net yield",
    m_net_yield_sub: "Your yearly rental return after running costs, expressed as a percentage of what the property cost you including all fees.",
    m_annual_cf: "Annual cash flow",
    m_annual_cf_sub: "The cash you keep each year after paying operating costs and, if applicable, mortgage instalments.",
    m_coc: "Cash on cash",
    m_coc_sub: "How hard the money you actually paid at close is working for you every year, as a percentage.",
    econ_title: "Deal economics",
    econ_price: "Purchase price",
    econ_price_sqft: "Price / sqft",
    econ_dld: "DLD fee",
    econ_broker: "Broker fee",
    econ_other: "Other purchase costs",
    econ_acq_fees: "Total acquisition costs (fees only)",
    econ_acq_total: "Total acquisition cost (price + fees)",
    econ_rent: "Annual rent (entered)",
    econ_eff_rent: "Effective annual rent (after vacancy)",
    econ_opex: "Annual operating costs",
    econ_noi: "Net operating income",
    fin_out_title: "Financing outcome",
    fin_out_dp: "Down payment",
    fin_out_loan: "Mortgage amount",
    fin_out_monthly: "Monthly payment",
    fin_out_annual: "Annual debt service",
    fin_out_costs: "Mortgage-related costs",
    fin_out_initial: "Initial cash required",
    req_title: "What rent do I need?",
    req_sub: "Reverse math from your target net yield.",
    req_annual: "Required annual rent",
    req_monthly: "Required monthly rent",
    req_entered: "Your entered annual rent",
    req_diff: "Rent gap",
    req_diff_short: "below the modeled rent required to reach your target net yield.",
    req_diff_beats: "above the modeled rent required to reach your target net yield.",
    compare_title: "Cash vs Mortgage",
    compare_sub: "Same deal, two financing structures. We show both. You decide.",
    compare_cash: "Cash",
    compare_mortgage: "Mortgage",
    compare_initial: "Initial cash required",
    compare_debt: "Annual debt service",
    compare_cf: "Annual cash flow",
    compare_coc: "Cash-on-cash %",
    compare_equity: "Equity at horizon",
    compare_cum_cf: "Cumulative cash flow at horizon",
    projection_title: "Year-by-year scenario",
    projection_sub_on: "Based on the growth assumptions you entered.",
    projection_sub_off: "Growth is OFF. Every year mirrors Year 1. Toggle the Optional section to enter assumptions.",
    y_year: "Yr", y_val: "Scenario value", y_rent: "Annual rent", y_opex: "Annual opex",
    y_noi: "NOI", y_debt: "Debt service", y_cf: "Cash flow", y_cumcf: "Cum. cash flow",
    y_loan: "Remaining loan", y_equity: "Equity",
    disclaimer: "Propertic's Deal Analyzer provides mathematical calculations based on information and assumptions entered by you. Scenario projections are illustrative and are not forecasts, valuations, investment recommendations or guarantees of future performance. Actual costs, rental income, financing terms, property values and other outcomes may differ.",
    cta_title: "Want to discuss your Dubai property purchase?",
    cta_sub: "Get a free consultation with a licensed Propertic advisor (RERA #43950). We can help you understand the buying process, financing options and transaction costs. No obligation.",
    faq_title: "Frequently asked questions",
    powered: "Powered by your inputs · zero market injection",
  },
  ar: {
    hero_kicker: "محلّل صفقات العقار",
    hero_title: "اكتشف الأرقام الحقيقية خلف السعر.",
    hero_sub: "أدخل أرقامك لتحسب تكلفة التملّك والعائد والتدفّق النقدي، وجرّب السيناريوهات التي تفترضها بنفسك. حساب رياضي محايد، لا يُعدّ توقّعاً أو تقييماً أو توصية استثمارية من بروبيرتك.",
    property_title: "العقار والمصروفات التشغيلية",
    field_price: "سعر الشراء",
    field_size: "المساحة (قدم مربّع)",
    field_size_hint: "اختياري، لاحتساب السعر لكل قدم مربّع",
    field_rent: "الإيجار السنوي",
    field_sc: "رسوم الخدمة السنوية",
    field_mgmt: "تكلفة الإدارة السنوية",
    field_mgmt_hint: "مثل شركة إدارة الإيجار قصير الأمد",
    field_other_opex: "مصروفات سنوية أخرى",
    field_other_opex_hint: "مثل خدمات الإيجار اليومي والتأمين والتبريد",
    acq_title: "تكاليف التملّك",
    field_dld: "رسم نقل الملكية / التسجيل %",
    field_dld_hint: "يختلف حسب الإمارة أو البلد. أدخل النسبة المطبّقة على معاملتك.",
    field_dld_aed_note: "مرجع دبي: قد تختلف رسوم النقل حسب المعاملة. تحقّق من رسم دائرة الأراضي المطبّق قبل الاعتماد على الحساب.",
    field_broker: "رسوم الوسيط %",
    field_broker_hint: "عادةً ٢٪ للجاهز · ٠٪ للمخطّط",
    field_other_purchase: "تكاليف شراء أخرى",
    field_other_purchase_hint: "رسوم إدارة المطوّر، شهادة عدم ممانعة، تقييم، تأمين",
    fin_title: "التمويل",
    mode_cash: "نقداً",
    mode_mortgage: "رهن عقاري",
    field_dp_pct: "الدفعة الأولى %",
    field_rate: "سعر الفائدة %",
    field_term: "مدّة القرض (سنوات)",
    field_mortgage_costs: "تكاليف مرتبطة بالرهن",
    field_mortgage_costs_hint: "رسوم الترتيب والتقييم والتأمين، تُدفع مرّة واحدة",
    err_dp: "أدخل نسبة دفعة أولى أكبر من صفر",
    err_rate: "أدخل سعر الفائدة",
    err_term: "أدخل مدّة القرض بالسنوات",
    target_title: "العائد المستهدف",
    field_target_yield: "صافي العائد المستهدف %",
    field_target_yield_hint: "يُستخدم لحساب الإيجار المطلوب أدناه",
    scenario_title: "اختياري: أضف افتراضاتك للنمو",
    scenario_note: "معطّل افتراضياً. أي قيم نمو تدخلها هي افتراضاتك أنت، وليست توقّعاً أو تقييماً من بروبيرتك. عند إبقائه معطّلاً تبقى قيمة العقار والإيجار والمصروفات ثابتة، بينما يتغيّر رصيد القرض وحقوق الملكية مع السداد. يمكن أن تكون القيم سالبة أو موجبة (مثلاً: −٣٪ لسيناريو تراجع، أو +٥٪ لسيناريو صعود).",
    field_appreciation: "تغيّر قيمة العقار % / سنوياً",
    field_rent_growth: "نمو الإيجار % / سنوياً",
    field_expense_growth: "نمو المصروفات % / سنوياً",
    field_vacancy: "نسبة الشغور % / سنوياً",
    field_holding: "مدّة الاحتفاظ (سنوات)",
    field_growth_hint: "يمكن أن تكون موجبة أو سالبة",
    metrics_title: "مؤشّرات الصفقة",
    m_break_even: "إيجار التعادل النقدي",
    m_break_even_sub: "أدنى إيجار سنوي يمكنك تحصيله وتغطية جميع مصروفات التشغيل وأقساط الرهن إن وُجدت. أي إيجار أقل يعني تشغيل العقار بخسارة نقدية.",
    m_net_yield: "صافي العائد",
    m_net_yield_sub: "عائد الإيجار السنوي بعد المصروفات التشغيلية، معبّراً عنه بنسبة مئوية من إجمالي تكلفة التملّك بما فيها الرسوم.",
    m_annual_cf: "التدفّق النقدي السنوي",
    m_annual_cf_sub: "النقد الذي يتبقّى لك سنوياً بعد سداد المصروفات التشغيلية وأقساط الرهن (إن وُجدت).",
    m_coc: "العائد النقدي",
    m_coc_sub: "العائد السنوي على المبلغ النقدي الذي دفعته فعلياً عند إتمام الشراء، كنسبة مئوية.",
    econ_title: "تفاصيل الصفقة",
    econ_price: "سعر الشراء",
    econ_price_sqft: "السعر / قدم²",
    econ_dld: "رسم دائرة الأراضي",
    econ_broker: "رسوم الوسيط",
    econ_other: "تكاليف شراء أخرى",
    econ_acq_fees: "إجمالي رسوم التملّك",
    econ_acq_total: "إجمالي تكلفة التملّك (السعر + الرسوم)",
    econ_rent: "الإيجار السنوي (المُدخل)",
    econ_eff_rent: "الإيجار الفعلي (بعد الشغور)",
    econ_opex: "المصروفات التشغيلية السنوية",
    econ_noi: "الدخل التشغيلي الصافي",
    fin_out_title: "نتيجة التمويل",
    fin_out_dp: "الدفعة الأولى",
    fin_out_loan: "مبلغ الرهن",
    fin_out_monthly: "القسط الشهري",
    fin_out_annual: "خدمة الدين السنوية",
    fin_out_costs: "تكاليف مرتبطة بالرهن",
    fin_out_initial: "النقد المطلوب ابتداءً",
    req_title: "ما هو الإيجار الذي أحتاجه؟",
    req_sub: "حساب عكسي انطلاقاً من العائد المستهدف.",
    req_annual: "الإيجار السنوي المطلوب",
    req_monthly: "الإيجار الشهري المطلوب",
    req_entered: "الإيجار السنوي الذي أدخلته",
    req_diff: "فجوة الإيجار",
    req_diff_short: "أقلّ من الإيجار السنوي المطلوب للوصول إلى صافي العائد المستهدف.",
    req_diff_beats: "أعلى من الإيجار السنوي المطلوب للوصول إلى صافي العائد المستهدف.",
    compare_title: "نقداً مقابل الرهن العقاري",
    compare_sub: "الصفقة نفسها بطريقتي تمويل مختلفتين. نعرض لك الخيارين، والقرار لك.",
    compare_cash: "نقداً",
    compare_mortgage: "رهن",
    compare_initial: "النقد المطلوب ابتداءً",
    compare_debt: "خدمة الدين السنوية",
    compare_cf: "التدفّق النقدي السنوي",
    compare_coc: "العائد النقدي %",
    compare_equity: "حقوق الملكية في نهاية المدّة",
    compare_cum_cf: "التدفّق النقدي المتراكم في نهاية المدّة",
    projection_title: "سيناريو سنة بعد سنة",
    projection_sub_on: "استناداً إلى افتراضات النمو التي أدخلتها.",
    projection_sub_off: "افتراضات النمو معطّلة؛ لذلك تبقى قيمة العقار والإيجار والمصروفات التشغيلية ثابتة، أمّا رصيد القرض وحقوق الملكية فيتغيّران مع السداد، وتتوقّف أقساط الرهن عند انتهاء مدّة القرض. فعّل القسم الاختياري لإدخال افتراضاتك.",
    y_year: "السنة", y_val: "قيمة السيناريو", y_rent: "الإيجار السنوي", y_opex: "المصروفات السنوية",
    y_noi: "الدخل الصافي", y_debt: "خدمة الدين", y_cf: "التدفّق النقدي", y_cumcf: "التدفّق المتراكم",
    y_loan: "الرصيد المتبقّي", y_equity: "حقوق الملكية",
    disclaimer: "يوفّر محلّل الصفقات من بروبيرتك حسابات رياضية استناداً إلى المعلومات والافتراضات التي تُدخلها. توقّعات السيناريو توضيحية فقط وليست تنبؤات أو تقييمات أو توصيات استثمارية أو ضمانات لأداء مستقبلي. قد تختلف التكاليف والدخل الإيجاري وشروط التمويل وقيم العقارات والنتائج الأخرى الفعلية عمّا هو معروض.",
    cta_title: "هل ترغب في مناقشة عملية الشراء في دبي؟",
    cta_sub: "احصل على استشارة مجانية مع مستشار بروبيرتك المرخّص (RERA #43950). يمكننا مساعدتك على فهم إجراءات الشراء وخيارات التمويل وتكاليف المعاملة. دون أي التزام.",
    faq_title: "الأسئلة الشائعة",
    powered: "يعتمد على مدخلاتك فقط · دون إضافة أي بيانات سوقية",
  },
};

// ---------- FAQ — 12 curated, professional Arabic (not literal) ----------
const FAQ = {
  en: [
    { q: "What is the Dubai Property Deal Analyzer?", a: "The Propertic Dubai Property Deal Analyzer is a free calculator that helps you understand the financial economics of a Dubai property purchase. You enter the purchase price, rent, service charges, acquisition costs and financing terms, and the tool returns metrics such as total acquisition cost, gross and net yield, annual cash flow and cash on cash return." },
    { q: "How is gross rental yield calculated?", a: "Gross rental yield equals annual rental income divided by purchase price, multiplied by 100. For example, a property purchased at AED 2,000,000 with annual rent of AED 120,000 has a gross yield of 6%. The calculation does not deduct operating expenses or financing costs." },
    { q: "How is net rental yield calculated?", a: "Net yield takes annual operating expenses into account. Our calculator computes net yield using the income and expenses you enter, divided by the total acquisition cost. Because it uses your own inputs, the result depends entirely on the values you provide." },
    { q: "What is included in total acquisition cost?", a: "Total acquisition cost equals the purchase price plus every acquisition expense you enter, including DLD registration fee, broker fees, and any other purchase costs such as developer admin charges, NOC and valuation. Each item is shown separately so you can trace every dirham." },
    { q: "What is the Dubai Land Department (DLD) registration fee?", a: "The calculator uses a default 4% assumption for Dubai's DLD sale registration fee. You can override it if you are analysing a property in another emirate or country. It is displayed as its own line item so you can see it separately from the price and the other acquisition expenses." },
    { q: "Does the calculator include mortgage payments?", a: "Yes. If you select mortgage financing you enter the down payment, interest rate, term and any mortgage related costs. The calculator returns the mortgage amount, monthly payment, annual debt service and resulting cash flow." },
    { q: "What is cash on cash return?", a: "Cash on cash return measures annual cash flow relative to the actual cash you have invested at close. For a financed purchase this is your down payment plus acquisition fees plus mortgage related costs. It is a useful indicator of how hard each dirham you funded is working." },
    { q: "What is break even rent?", a: "Break even rent is the minimum gross annual rent required for the property to cover all annual operating costs and, if applicable, mortgage debt service, while also accounting for any vacancy you assume. Below this rent the property runs at a loss on a cash flow basis." },
    { q: "What rent do I need to achieve a specific yield?", a: "The tool works backwards from a target net yield. Enter the yield you want and it returns the annual and monthly gross rent required to achieve it, based on your entered acquisition cost, operating costs and vacancy." },
    { q: "Can I compare buying with cash versus using a mortgage?", a: "Yes. The Cash vs Mortgage section shows the same deal under both financing structures side by side, including initial cash required, annual cash flow, debt service, cash on cash return and equity at your chosen horizon. The tool never labels either scenario as better or worse." },
    { q: "Does Propertic predict future property prices?", a: "No. The Deal Analyzer does not forecast property prices. Its multi-year section is a scenario model where you enter your own assumptions and the tool shows how those specific assumptions mathematically affect the results. It does not classify properties as good or bad investments and does not provide any investment recommendation." },
    { q: "Is the Dubai Property Deal Analyzer free?", a: "Yes. The Deal Analyzer is a free Propertic tool. You can calculate acquisition costs, rental yields, cash flow and your own scenarios without paying to use it." },
  ],
  ar: [
    { q: "ما هو محلّل صفقات العقار في دبي؟", a: "محلّل صفقات العقار من بروبيرتك أداة مجانية تساعدك على فهم اقتصاد شراء عقار في دبي. تُدخل السعر والإيجار ورسوم الخدمة وتكاليف التملّك وشروط التمويل، فتظهر لك المؤشّرات الأساسية: إجمالي تكلفة التملّك، إجمالي وصافي العائد، التدفّق النقدي السنوي، والعائد النقدي على رأس المال المستثمر." },
    { q: "كيف يُحسب إجمالي عائد الإيجار؟", a: "إجمالي عائد الإيجار = الإيجار السنوي ÷ سعر الشراء × ١٠٠. مثال: عقار بسعر ٢,٠٠٠,٠٠٠ درهم وإيجار سنوي ١٢٠,٠٠٠ درهم يعطي عائداً إجمالياً بنسبة ٦٪. هذا الحساب لا يخصم المصروفات التشغيلية ولا تكاليف التمويل." },
    { q: "كيف يُحسب صافي عائد الإيجار؟", a: "صافي العائد يأخذ المصروفات التشغيلية السنوية في الاعتبار. يحسبه المحلّل بقسمة الدخل التشغيلي الصافي (الإيجار الفعلي مطروحاً منه المصروفات) على إجمالي تكلفة التملّك. النتيجة تعتمد كلياً على القيم التي تُدخلها أنت." },
    { q: "ما الذي يشمله إجمالي تكلفة التملّك؟", a: "إجمالي تكلفة التملّك = سعر الشراء + كل بند تُدخله من رسوم التملّك، بما فيها رسم دائرة الأراضي، ورسوم الوسيط، وأي مصروفات شراء أخرى مثل رسوم إدارة المطوّر وشهادة عدم الممانعة والتقييم. يعرض المحلّل كل بند على حدة لتتبّع كل درهم." },
    { q: "ما هو رسم تسجيل دائرة الأراضي (DLD)؟", a: "يستخدم المحلّل افتراضياً ٤٪ لرسم بيع دائرة الأراضي في دبي. يمكنك تعديل النسبة إذا كنت تحلّل عقاراً في إمارة أو بلد آخر. يظهر البند كسطر مستقلّ لتراه بمعزل عن السعر وبقيّة رسوم التملّك." },
    { q: "هل يشمل المحلّل أقساط الرهن العقاري؟", a: "نعم. عند اختيار خيار الرهن العقاري تُدخل الدفعة الأولى وسعر الفائدة والمدّة وأي تكاليف مرتبطة بالرهن، فيُعيد المحلّل مبلغ الرهن والقسط الشهري وخدمة الدين السنوية والتدفّق النقدي الناتج." },
    { q: "ما هو العائد النقدي (Cash on Cash)؟", a: "العائد النقدي يقيس التدفّق النقدي السنوي مقارنةً بالنقد الذي استثمرته فعلاً عند إتمام الصفقة، أي الدفعة الأولى + رسوم التملّك + أي تكاليف مرتبطة بالرهن. مؤشّر مفيد لمعرفة مدى فاعلية كل درهم دفعته من رأس مالك." },
    { q: "ما هو إيجار التعادل النقدي؟", a: "إيجار التعادل هو الحدّ الأدنى للإيجار السنوي الإجمالي الذي يغطّي جميع المصروفات التشغيلية وخدمة الدين (إن وُجدت) بعد احتساب الشغور الذي افترضته. الإيجار الأقلّ من هذا الحدّ يعني تشغيل العقار بخسارة نقدية." },
    { q: "ما هو الإيجار الذي أحتاجه لتحقيق عائد معيّن؟", a: "يعمل المحلّل بطريقة عكسية انطلاقاً من العائد الصافي المستهدف. تُدخل النسبة المطلوبة فيُعيد إليك الإيجار السنوي والشهري المطلوبَين لتحقيقها، بناءً على تكلفة التملّك والمصروفات والشغور الذي أدخلته." },
    { q: "هل يمكنني مقارنة الشراء نقداً بالشراء عبر رهن عقاري؟", a: "نعم. يعرض قسم «نقداً مقابل الرهن» الصفقة نفسها بهيكلَي التمويل جنباً إلى جنب، بما فيها النقد المطلوب ابتداءً، والتدفّق النقدي السنوي، وخدمة الدين، والعائد النقدي، وحقوق الملكية عند الأفق الذي تختاره. لا يصنّف المحلّل أحد الخيارَين بأنّه أفضل من الآخر." },
    { q: "هل تتنبّأ بروبيرتك بأسعار العقارات المستقبلية؟", a: "لا. لا يقدّم المحلّل توقّعات لأسعار العقارات. القسم متعدّد السنوات هو نموذج سيناريو تُدخل فيه افتراضاتك أنت، ويعرض المحلّل كيف تؤثّر تلك الافتراضات تحديداً على النتائج. كما لا يصنّف المحلّل العقارات ولا يقدّم أي توصية استثمارية." },
    { q: "هل محلّل صفقات العقار في دبي مجاني؟", a: "نعم. محلّل الصفقات أداة مجانية من بروبيرتك. يمكنك حساب تكاليف التملّك وعوائد الإيجار والتدفّق النقدي وسيناريوهاتك الخاصّة دون أي رسوم." },
  ],
};

// ---------- Reusable UI atoms ----------
const CardShell = ({ title, subtitle, children, testId }) => (
  <div className="bg-white border hairline rounded-2xl p-5 md:p-6 shadow-sm" data-testid={testId}>
    {title && (
      <div className="mb-4">
        <div className="text-[10px] uppercase tracking-[0.18em] text-propertic-navy/60 font-semibold">{title}</div>
        {subtitle && <div className="text-sm text-propertic-navy/70 mt-1">{subtitle}</div>}
      </div>
    )}
    {children}
  </div>
);

const Tip = ({ text, isRTL }) => (
  <span
    className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-propertic-gold/60 text-propertic-gold text-[10px] leading-none cursor-help align-middle"
    style={{ marginInlineStart: 6 }}
    title={text}
    aria-label={text}
  >
    ?
  </span>
);

const Field = ({ label, hint, value, onChange, step = 1, disabled, error, allowNegative, testId, suffix, footer, info, min, max }) => {
  const [text, setText] = useState(String(value ?? 0));
  const focusedRef = useRef(false);
  useEffect(() => {
    // Only sync from parent when the input is NOT being edited. This prevents
    // the parent's numeric round-trip (0 → "0") from clobbering the user's
    // in-progress typing (typing "5" would otherwise flash back to "0").
    if (!focusedRef.current) setText(String(value ?? 0));
  }, [value]);
  const pattern = allowNegative ? /^-?\d*\.?\d*$/ : /^\d*\.?\d*$/;
  // Clamp a numeric value to [min, max] when either bound is provided. Runs
  // on blur so the user isn't fighting the input while typing.
  const clamp = (n) => {
    if (min != null && n < min) return min;
    if (max != null && n > max) return max;
    return n;
  };
  return (
    <label className={`flex flex-col gap-1 ${disabled ? "opacity-50 pointer-events-none" : ""}`}>
      <span className="text-[12px] font-medium text-propertic-navy/80 min-h-[36px] leading-snug block">
        {label}
        {info && <InfoTip {...info} />}
        {hint && <span className="text-propertic-navy/45 font-normal ms-2">{hint}</span>}
      </span>
      <div className={`relative flex items-center bg-white border ${error ? "border-red-400" : "hairline"} rounded-xl h-11 px-3 focus-within:border-propertic-gold transition-colors`}>
        <input
          type="text"
          inputMode="decimal"
          value={text}
          onFocus={() => {
            focusedRef.current = true;
            // If the current value is exactly 0, clear the field so the user
            // types into an empty box instead of prepending digits to "0".
            if (text === "0" || text === "0.0") setText("");
          }}
          onBlur={() => {
            focusedRef.current = false;
            if (text === "" || text === "-" || text === ".") {
              const zero = clamp(0);
              setText(String(zero));
              onChange(zero);
              return;
            }
            // Strip leading zeros / normalize (e.g. "08500" → "8500", "-0.50" → "-0.5").
            const raw = Number(normalizeDigits(text));
            if (Number.isFinite(raw)) {
              const n = clamp(raw);
              setText(String(n));
              onChange(n);
            }
          }}
          onChange={(e) => {
            // Convert Arabic-Indic / Persian digits (and ٫) to Latin so both
            // scripts work in the same input.
            const raw = normalizeDigits(e.target.value);
            if (raw !== "" && !pattern.test(raw)) return;
            setText(raw);
            if (raw === "" || raw === "-" || raw === ".") return;
            const n = Number(raw);
            if (!Number.isNaN(n)) onChange(n);
          }}
          disabled={disabled}
          className="w-full bg-transparent outline-none text-propertic-navy text-sm tabular"
          data-testid={testId}
        />
        {suffix && <span className="text-propertic-navy/50 text-xs ms-2 tabular whitespace-nowrap">{suffix}</span>}
      </div>
      {error && (
        <span className="text-[11px] text-red-500 flex items-center gap-1 mt-0.5">
          <AlertCircle className="w-3 h-3" /> {error}
        </span>
      )}
      {footer && <span className="text-[11px] text-propertic-navy/50 mt-1 leading-snug">{footer}</span>}
    </label>
  );
};

/**
 * InfoTip — small "i" chip that reveals a rich tooltip on hover (desktop)
 * and tap (mobile). Closes on click outside. Content: title, short
 * plain-English description, formula (mono), worked example using live
 * numbers where useful.
 */
const InfoTip = ({ title, description, formula, example, dark = false }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("touchstart", onDoc); };
  }, [open]);
  return (
    <span ref={ref} className="relative inline-block" style={{ marginInlineStart: 6 }}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); setOpen((v) => !v); }}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        className={`inline-flex items-center justify-center w-4 h-4 rounded-full border text-[10px] leading-none align-middle transition-opacity ${
          dark ? "border-propertic-gold-2/60 text-propertic-gold-2 hover:text-propertic-gold-2" : "border-propertic-navy/40 text-propertic-navy/60 hover:text-propertic-navy"
        }`}
        aria-label={title}
      >
        i
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
            className="absolute z-50 top-6 start-0 w-72 max-w-[calc(100vw-32px)] bg-propertic-navy text-propertic-paper rounded-xl p-3 shadow-2xl text-xs leading-relaxed"
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
          >
            <div className="font-semibold text-propertic-gold-2 text-[10px] uppercase tracking-[0.14em] mb-1.5">{title}</div>
            {description && <p className="text-propertic-paper/85 mb-2">{description}</p>}
            {formula && (
              <div className="bg-white/8 border border-white/10 rounded px-2 py-1 mb-2 font-mono text-[10.5px] text-propertic-paper/95">
                {formula}
              </div>
            )}
            {example && <p className="text-propertic-paper/65 italic text-[11px]">{example}</p>}
          </motion.div>
        )}
      </AnimatePresence>
    </span>
  );
};

// ---------- Main page ----------
export default function PropertyDealAnalyzer() {
  const { lang, isRTL } = useLang();
  const t = COPY[lang === "ar" ? "ar" : "en"];
  const isAR = lang === "ar";
  const faqList = FAQ[isAR ? "ar" : "en"];

  // Local AED/USD toggle (mirrors MortgageCalculator pattern)
  const { rates } = useCurrency();
  const [dispCurrency, setDispCurrency] = useState(() => {
    try { return localStorage.getItem("propertic_deal_currency") || "AED"; } catch { return "AED"; }
  });
  useEffect(() => {
    try { localStorage.setItem("propertic_deal_currency", dispCurrency); } catch {}
  }, [dispCurrency]);
  const isUSD = dispCurrency === "USD";
  const usdPerAed = Math.max(rates?.USD || (1 / AED_PER_USD_PEG), 1e-6);
  const aedPerUsd = 1 / usdPerAed;
  const toDisp = (aed) => (isUSD ? aed * usdPerAed : aed);
  const fromDisp = (v) => (isUSD ? Number(v || 0) * aedPerUsd : Number(v || 0));
  const currencyLabel = isUSD ? (isAR ? "دولار" : "USD") : (isAR ? "درهم" : "AED");

  // Money formatter (bilingual + currency-aware, integer for display).
  // Uses fixArThousands to swap the Arabic thousands sep (٬, elevated) for
  // a baseline comma so results read cleanly in AR.
  const fmtMoney = (aed) => {
    const disp = toDisp(aed);
    const rounded = Math.round(disp);
    const localeStr = isAR ? fixArThousands(rounded.toLocaleString("ar-EG")) : rounded.toLocaleString("en-US");
    return isAR
      ? `${localeStr} ${currencyLabel}`
      : `${currencyLabel} ${localeStr}`;
  };
  const fmtPct = (n) => {
    if (!Number.isFinite(n)) return "—";
    const s = n.toFixed(2);
    return isAR ? `${arDigits(s)}٪` : `${s}%`;
  };
  const fmtInt = (n) => {
    if (!Number.isFinite(n)) return "—";
    return isAR
      ? fixArThousands(Math.round(n).toLocaleString("ar-EG"))
      : Math.round(n).toLocaleString("en-US");
  };

  // ---------- INPUT STATE (all money stored in AED internally) ----------
  const [state, setState] = useState({
    purchasePriceAed: 2000000,
    sizeSqft: 1200,
    annualRentAed: 130000,
    serviceChargesAed: 12000,
    managementCostAed: 6500,
    otherAnnualExpensesAed: 1500,
    dldFeePct: 4,
    brokerFeePct: 2,
    otherPurchaseCostsAed: 40000,
    mode: "mortgage",
    downPaymentPct: 25,
    interestRatePct: 3.9,
    termYears: 25,
    mortgageCostsAed: 15000,
    targetNetYieldPct: 5,
    appreciationPct: 0,
    rentGrowthPct: 0,
    expenseGrowthPct: 0,
    vacancyPct: 0,
    holdingYears: 10,
  });
  const [scenarioOn, setScenarioOn] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [showFormulas, setShowFormulas] = useState(false);

  const setAed = (key) => (dispVal) => setState((p) => ({ ...p, [key]: fromDisp(dispVal) }));
  const setNum = (key) => (v) => setState((p) => ({ ...p, [key]: v }));

  const isCash = state.mode === "cash";

  // Validation errors — only shown in mortgage mode
  const errors = useMemo(() => {
    if (isCash) return {};
    const e = {};
    if (!(state.downPaymentPct > 0)) e.downPaymentPct = t.err_dp;
    if (!(state.interestRatePct > 0)) e.interestRatePct = t.err_rate;
    if (!(state.termYears > 0)) e.termYears = t.err_term;
    return e;
  }, [isCash, state.downPaymentPct, state.interestRatePct, state.termYears, t]);
  const hasErrors = Object.keys(errors).length > 0;

  // Engine inputs — collapse mortgage residue to 0 when in cash mode
  // (defence-in-depth: engine also handles this but the UI zeroing keeps
  // the displayed comparison clean).
  const engineInputs = useMemo(() => {
    const opex = state.serviceChargesAed + state.managementCostAed + state.otherAnnualExpensesAed;
    return {
      purchasePrice: state.purchasePriceAed,
      sizeSqft: state.sizeSqft,
      annualRent: state.annualRentAed,
      annualOperatingCosts: opex,
      dldFeePct: state.dldFeePct,
      brokerFeePct: state.brokerFeePct,
      otherPurchaseCosts: state.otherPurchaseCostsAed,
      mode: state.mode,
      downPaymentPct: state.downPaymentPct,
      interestRatePct: state.interestRatePct,
      // Keep valid financing assumptions so the greyed mortgage comparator in cash
      // mode never runs a zero-term loan; the active cash result ignores them.
      termYears: isCash && !(state.termYears > 0) ? 25 : state.termYears,
      mortgageCosts: state.mortgageCostsAed,
      targetNetYieldPct: state.targetNetYieldPct,
      appreciationPct: scenarioOn ? state.appreciationPct : 0,
      rentGrowthPct: scenarioOn ? state.rentGrowthPct : 0,
      expenseGrowthPct: scenarioOn ? state.expenseGrowthPct : 0,
      vacancyPct: scenarioOn ? state.vacancyPct : 0,
      holdingYears: state.holdingYears,
    };
  }, [state, isCash, scenarioOn]);

  const out = useMemo(() => analyzeDeal(engineInputs), [engineInputs]);

  // ---------- SEO ----------
  const seoTitle = isAR
    ? "محلّل صفقات العقار المجاني | حاسبة عقارية سهلة الاستخدام"
    : "Free Property Deal Analyzer | Easy Real Estate Calculator";
  const seoDesc = isAR
    ? "حلّل أي صفقة عقارية في دقائق مع حاسبتنا المجانية سهلة الاستخدام. احسب عائد الإيجار والتدفّق النقدي والعائد على الاستثمار بالدرهم أو الدولار."
    : "Analyze any property deal in minutes with our free, easy-to-use property calculator. Calculate rental yield, cash flow, ROI and more in AED or USD.";
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqList.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  const appJsonLd = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: isAR ? "محلّل صفقات العقار في دبي" : "Dubai Property Deal Analyzer",
    description: seoDesc,
    applicationCategory: "FinanceApplication",
    offers: { "@type": "Offer", price: "0", priceCurrency: "AED" },
    operatingSystem: "Any",
    inLanguage: isAR ? "ar-AE" : "en-AE",
  };

  return (
    <div className={`min-h-screen bg-propertic-sand text-propertic-navy ${isRTL ? "text-right" : ""}`} data-testid="deal-analyzer-page">
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={isRTL ? "https://thepropertic.ae/property-deal-analyzer?lang=ar" : "https://thepropertic.ae/property-deal-analyzer"} />
        <link rel="alternate" hrefLang="en" href="https://thepropertic.ae/property-deal-analyzer" />
        <link rel="alternate" hrefLang="ar" href="https://thepropertic.ae/property-deal-analyzer?lang=ar" />
        <link rel="alternate" hrefLang="x-default" href="https://thepropertic.ae/property-deal-analyzer" />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(appJsonLd)}</script>
      </Helmet>

      {/* HERO */}
      <section className="bg-propertic-navy text-propertic-paper">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-14 md:py-20">
          <div className="flex flex-col-reverse sm:flex-row items-start justify-between gap-5 sm:gap-8 sm:flex-wrap">
            <div className="w-full sm:flex-1 min-w-0 max-w-4xl">
              <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-propertic-gold-2">
                <Calculator className="w-3.5 h-3.5" /> {t.hero_kicker}
              </div>
              <h1 className="font-editorial text-[34px] sm:text-4xl md:text-5xl lg:text-6xl leading-[1.08] mt-4">{t.hero_title}</h1>
              <p className="text-propertic-paper/75 mt-5 text-[15px] md:text-base leading-relaxed max-w-2xl">{t.hero_sub}</p>
              <div className="mt-5 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-propertic-gold-2/80">
                <Sparkles className="w-3.5 h-3.5" /> {t.powered}
              </div>
            </div>

            {/* AED / USD toggle */}
            <div className="inline-flex items-center bg-white/8 border border-white/12 rounded-full p-1" data-testid="deal-currency-toggle">
              {["AED", "USD"].map((c) => (
                <button
                  key={c}
                  onClick={() => setDispCurrency(c)}
                  className={`px-4 h-9 rounded-full text-xs font-medium transition-colors ${
                    dispCurrency === c
                      ? "bg-propertic-gold text-propertic-navy"
                      : "text-propertic-paper/75 hover:text-propertic-paper"
                  }`}
                  data-testid={`deal-currency-${c.toLowerCase()}`}
                >
                  {isAR ? (c === "USD" ? "دولار" : "درهم") : c}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-5 md:px-8 -mt-8 pb-16 space-y-6">
        {/* PROPERTY + OPERATING */}
        <CardShell title={t.property_title} testId="deal-card-property">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label={t.field_price} value={Math.round(toDisp(state.purchasePriceAed))} onChange={setAed("purchasePriceAed")} step={5000} suffix={currencyLabel} testId="deal-input-price" min={0} />
            <Field label={t.field_size} hint={t.field_size_hint} value={state.sizeSqft} onChange={setNum("sizeSqft")} step={10} suffix={isAR ? "قدم²" : "sqft"} testId="deal-input-size" min={0} />
            <Field label={t.field_rent} value={Math.round(toDisp(state.annualRentAed))} onChange={setAed("annualRentAed")} step={1000} suffix={currencyLabel} testId="deal-input-rent" min={0} />
            <Field label={t.field_sc} value={Math.round(toDisp(state.serviceChargesAed))} onChange={setAed("serviceChargesAed")} step={500} suffix={currencyLabel} testId="deal-input-sc" min={0} />
            <Field label={t.field_mgmt} hint={t.field_mgmt_hint} value={Math.round(toDisp(state.managementCostAed))} onChange={setAed("managementCostAed")} step={500} suffix={currencyLabel} testId="deal-input-mgmt" min={0} />
            <Field label={t.field_other_opex} hint={t.field_other_opex_hint} value={Math.round(toDisp(state.otherAnnualExpensesAed))} onChange={setAed("otherAnnualExpensesAed")} step={250} suffix={currencyLabel} testId="deal-input-other-opex" min={0} />
          </div>
        </CardShell>

        {/* ACQUISITION */}
        <CardShell title={t.acq_title} testId="deal-card-acquisition">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label={t.field_dld} hint={t.field_dld_hint} value={state.dldFeePct} onChange={setNum("dldFeePct")} step={0.1} suffix="%" testId="deal-input-dld" min={0} max={100} footer={dispCurrency === "AED" ? t.field_dld_aed_note : undefined} />
            <Field label={t.field_broker} hint={t.field_broker_hint} value={state.brokerFeePct} onChange={setNum("brokerFeePct")} step={0.1} suffix="%" testId="deal-input-broker" min={0} max={100} />
            <Field label={t.field_other_purchase} hint={t.field_other_purchase_hint} value={Math.round(toDisp(state.otherPurchaseCostsAed))} onChange={setAed("otherPurchaseCostsAed")} step={500} suffix={currencyLabel} testId="deal-input-other-purchase" min={0} />
          </div>
        </CardShell>

        {/* FINANCING */}
        <CardShell title={t.fin_title} testId="deal-card-financing">
          {/* mode pills */}
          <div className="inline-flex items-center bg-propertic-sand border hairline rounded-full p-1 mb-5">
            {["cash", "mortgage"].map((m) => (
              <button
                key={m}
                onClick={() => setState((p) => ({ ...p, mode: m }))}
                className={`px-5 h-9 rounded-full text-xs font-medium transition-colors ${
                  state.mode === m
                    ? "bg-propertic-navy text-propertic-paper"
                    : "text-propertic-navy/70 hover:text-propertic-navy"
                }`}
                data-testid={`deal-mode-${m}`}
              >
                {m === "cash" ? t.mode_cash : t.mode_mortgage}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Field label={t.field_dp_pct} value={state.downPaymentPct} onChange={setNum("downPaymentPct")} step={1} suffix="%" disabled={isCash} error={errors.downPaymentPct} testId="deal-input-dp" min={0} max={100} />
            <Field label={t.field_rate} value={state.interestRatePct} onChange={setNum("interestRatePct")} step={0.1} suffix="%" disabled={isCash} error={errors.interestRatePct} testId="deal-input-rate" min={0} max={30} />
            <Field label={t.field_term} value={state.termYears} onChange={setNum("termYears")} step={1} suffix={isAR ? "سنة" : "yrs"} disabled={isCash} error={errors.termYears} testId="deal-input-term" min={0} max={40} />
            <Field label={t.field_mortgage_costs} hint={t.field_mortgage_costs_hint} value={Math.round(toDisp(state.mortgageCostsAed))} onChange={setAed("mortgageCostsAed")} step={500} suffix={currencyLabel} disabled={isCash} testId="deal-input-mcosts" min={0} />
          </div>
        </CardShell>

        {/* OPTIONAL SCENARIO */}
        <div className={`bg-white border-2 ${scenarioOn ? "border-propertic-gold" : "hairline border"} rounded-2xl p-5 md:p-6 transition-colors`} data-testid="deal-card-scenario">
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={scenarioOn}
              onChange={(e) => {
                const on = e.target.checked;
                setScenarioOn(on);
                if (!on) {
                  setState((p) => ({ ...p, appreciationPct: 0, rentGrowthPct: 0, expenseGrowthPct: 0, vacancyPct: 0 }));
                }
              }}
              className="mt-1 w-4 h-4 accent-propertic-gold"
              data-testid="deal-scenario-toggle"
            />
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] font-semibold" style={{ color: scenarioOn ? "var(--p-navy)" : "rgba(11,26,44,0.55)" }}>
                {t.scenario_title}
              </div>
              <div className="text-xs text-propertic-navy/60 mt-1 leading-relaxed">{t.scenario_note}</div>
            </div>
          </label>
          <AnimatePresence initial={false}>
            {scenarioOn && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
                className="overflow-hidden"
              >
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-5">
                  <Field label={t.field_appreciation} hint={t.field_growth_hint} value={state.appreciationPct} onChange={setNum("appreciationPct")} step={0.5} allowNegative suffix="%" testId="deal-input-apprec" min={-50} max={50} info={{ title: isAR ? "تغيّر قيمة العقار" : "Property value change", description: isAR ? "النسبة السنوية التي يطبّقها النموذج على قيمة العقار. هذه افتراضك أنت، وليست توقّع سوق من بروبيرتك." : "The annual percentage change the model applies to the property's modeled value. This is your assumption, not a Propertic market forecast.", example: isAR ? "مثال: ٣٪ تعني زيادة القيمة المحاكاة بحوالي ٣٪ كل سنة. القيم السالبة مسموحة." : "3% increases modeled value by ~3% each year. Negative values are allowed." }} />
                  <Field label={t.field_rent_growth} hint={t.field_growth_hint} value={state.rentGrowthPct} onChange={setNum("rentGrowthPct")} step={0.5} allowNegative suffix="%" testId="deal-input-rentgrowth" min={-50} max={50} info={{ title: isAR ? "نمو الإيجار" : "Rent growth", description: isAR ? "النسبة السنوية التي يطبّقها النموذج على الإيجار. افتراضك، لا توقّع سوقي." : "The annual percentage change applied to rent. Your assumption, not a rental market forecast.", example: isAR ? "٥٪ يرفع الإيجار السنوي المحاكى بنسبة ٥٪ سنوياً." : "5% grows modeled annual rent by 5% each year." }} />
                  <Field label={t.field_expense_growth} hint={t.field_growth_hint} value={state.expenseGrowthPct} onChange={setNum("expenseGrowthPct")} step={0.5} allowNegative suffix="%" testId="deal-input-expgrowth" min={-50} max={50} info={{ title: isAR ? "نمو المصروفات" : "Expense growth", description: isAR ? "النسبة السنوية التي يطبّقها النموذج على المصروفات التشغيلية." : "The annual percentage change applied to operating expenses.", example: isAR ? "٥٪ يزيد المصروفات المحاكاة بنسبة ٥٪ كل سنة." : "5% grows modeled operating expenses by 5% each year." }} />
                  <Field label={t.field_vacancy} value={state.vacancyPct} onChange={setNum("vacancyPct")} step={1} suffix="%" testId="deal-input-vacancy" min={0} max={100} info={{ title: isAR ? "نسبة الشغور" : "Vacancy %", description: isAR ? "النسبة المفترضة من الإيجار السنوي التي لا يتمّ تحصيلها بسبب الشغور." : "Percentage of potential annual rent assumed to be lost to vacancy.", example: isAR ? "١٠٪ تعني افتراض عدم تحصيل ما يقارب ١٠٪ من الإيجار." : "10% assumes ~10% of potential annual rent is not collected." }} />
                  <Field label={t.field_holding} value={state.holdingYears} onChange={setNum("holdingYears")} step={1} suffix={isAR ? "سنة" : "yrs"} testId="deal-input-holding" min={1} max={30} info={{ title: isAR ? "مدّة الاحتفاظ" : "Holding period", description: isAR ? "عدد السنوات المستخدم لإسقاط السيناريو." : "The number of years used for the scenario projection.", example: isAR ? "١٥ سنة تنتج سيناريو مدّته ١٥ سنة." : "15 years produces a 15 year modeled scenario." }} />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* GLOBAL MORTGAGE ERROR BANNER */}
        {hasErrors && (
          <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 flex items-center gap-2 text-sm" data-testid="deal-mortgage-errors">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{Object.values(errors).join(" · ")}</span>
          </motion.div>
        )}

        {/* HEADLINE METRICS */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {(() => {
            const be = out.returns.breakEvenAnnualRent;
            const cf = out.returns.annualCashFlow;
            const ny = out.returns.netYieldPct;
            const noi = out.returns.netOperatingIncome;
            const acqTot = out.acquisition.totalAcquisitionCost;
            const initCash = out.financing.initialCashRequired;
            const beText = Number.isFinite(be)
              ? (isAR
                  ? `استناداً إلى افتراضاتك، إيجار التعادل النقدي هو ${fmtMoney(be)}.`
                  : `Based on your assumptions, the modeled break even rent is ${fmtMoney(be)}.`)
              : "";
            const cfText = cf === 0
              ? (isAR ? "التدفّق النقدي متعادل عند الصفر بناءً على مدخلاتك." : "Modeled cash flow is exactly zero based on your inputs.")
              : cf > 0
                ? (isAR
                    ? `بحسب المدخلات والافتراضات التي أدخلتها، يُظهر النموذج تدفّقاً نقدياً سنوياً موجباً قدره ${fmtMoney(cf)} بعد المصروفات التشغيلية وأقساط الرهن، إن وُجدت.`
                    : `The property is modeled to generate ${fmtMoney(cf)} of annual cash flow after operating costs and any mortgage payments.`)
                : (isAR
                    ? `بحسب المدخلات والافتراضات التي أدخلتها، يُظهر النموذج عجزاً نقدياً سنوياً قدره ${fmtMoney(Math.abs(cf))} بعد المصروفات التشغيلية وأقساط الرهن.`
                    : `The property is modeled to have an annual cash shortfall of ${fmtMoney(Math.abs(cf))} after operating costs and mortgage payments.`);
            const metrics = [
              {
                label: t.m_break_even,
                sub: t.m_break_even_sub,
                value: Number.isFinite(be) ? fmtMoney(be) : "∞",
                accent: true,
                testId: "deal-metric-breakeven",
                dynamic: beText,
                info: {
                  title: isAR ? "إيجار التعادل النقدي" : "Cash flow break even rent",
                  description: isAR
                    ? "الحدّ الأدنى للإيجار السنوي الذي يجعل التدفّق النقدي يساوي صفراً بعد المصروفات التشغيلية وخدمة الدين والشغور."
                    : "The minimum annual rent required for the property to cover modeled operating costs and mortgage debt service, taking any vacancy into account. Rent below this figure means negative cash flow.",
                  formula: isAR ? "(المصروفات التشغيلية + خدمة الدين) ÷ (١ − نسبة الشغور)" : "(Opex + Debt service) ÷ (1 − Vacancy%)",
                  example: isAR
                    ? "استناداً إلى مدخلاتك، إذا أنقصت الإيجار عن هذا الرقم، ستحتاج إلى إيداع مال إضافي كل عام."
                    : "In this example, if rent falls below this number the owner would need to contribute cash each year to keep the property running.",
                },
              },
              {
                label: t.m_net_yield,
                sub: t.m_net_yield_sub,
                value: fmtPct(ny),
                testId: "deal-metric-netyield",
                info: {
                  title: isAR ? "صافي العائد" : "Net yield",
                  description: isAR
                    ? "قياس عائد التشغيل قبل التمويل. يحتسب الشغور والمصروفات التشغيلية. لا يخصم أقساط الرهن."
                    : "Measures operating return before financing. Accounts for vacancy and operating expenses. Does NOT subtract mortgage payments.",
                  formula: isAR ? "صافي الدخل التشغيلي ÷ إجمالي تكلفة التملّك × ١٠٠" : "NOI ÷ Total Acquisition Cost × 100",
                  example: isAR
                    ? `مثال: ${fmtMoney(noi)} صافي دخل تشغيلي ÷ ${fmtMoney(acqTot)} إجمالي تكلفة التملّك = ${fmtPct(ny)}.`
                    : `Example: ${fmtMoney(noi)} annual NOI ÷ ${fmtMoney(acqTot)} total acquisition cost = ${fmtPct(ny)}.`,
                },
              },
              {
                label: t.m_annual_cf,
                sub: t.m_annual_cf_sub,
                value: fmtMoney(cf),
                testId: "deal-metric-cf",
                dynamic: cfText,
                info: {
                  title: isAR ? "التدفّق النقدي السنوي" : "Annual cash flow",
                  description: isAR
                    ? "المبلغ المتبقّي أو العجز كل سنة بعد المصروفات التشغيلية وأقساط الرهن."
                    : "The amount left over, or shortfall, each year after operating costs and mortgage debt service.",
                  formula: isAR ? "صافي الدخل التشغيلي − خدمة الدين السنوية" : "NOI − Annual Debt Service",
                  example: isAR
                    ? `مثال: ${fmtMoney(noi)} صافي دخل − ${fmtMoney(out.financing.annualDebtService)} خدمة الدين = ${fmtMoney(cf)}.`
                    : `Example: ${fmtMoney(noi)} NOI − ${fmtMoney(out.financing.annualDebtService)} annual mortgage payments = ${fmtMoney(cf)}.`,
                },
              },
              {
                label: t.m_coc,
                sub: t.m_coc_sub,
                value: fmtPct(out.returns.cashOnCashPct),
                testId: "deal-metric-coc",
                info: {
                  title: isAR ? "العائد النقدي" : "Cash on cash",
                  description: isAR
                    ? "يقيس التدفّق النقدي السنوي مقارنةً بالنقد الذي دفعته فعلياً عند الإتمام. لا يمثّل إجمالي عائد الاستثمار."
                    : "Annual cash flow relative to the cash you actually invested at close. It is NOT a total investment return.",
                  formula: isAR ? "التدفّق النقدي السنوي ÷ النقد المستثمر عند الشراء × ١٠٠" : "Annual Cash Flow ÷ Initial Cash Required × 100",
                  example: isAR
                    ? `مثال: ${fmtMoney(cf)} تدفّق نقدي ÷ ${fmtMoney(initCash)} نقد أوّلي = ${fmtPct(out.returns.cashOnCashPct)}.`
                    : `Example: ${fmtMoney(cf)} annual cash flow ÷ ${fmtMoney(initCash)} initial cash required = ${fmtPct(out.returns.cashOnCashPct)}.`,
                },
              },
            ];
            return metrics.map((m) => (
              <div key={m.label} className={`rounded-2xl p-4 md:p-5 ${m.accent ? "bg-propertic-navy text-propertic-paper" : "bg-white border hairline"}`} data-testid={m.testId}>
                <div className={`flex items-start justify-between gap-2 text-[10px] uppercase tracking-[0.16em] font-semibold ${m.accent ? "text-propertic-gold-2" : "text-propertic-navy/55"}`}>
                  <span>{m.label}</span>
                  <InfoTip {...m.info} dark={m.accent} />
                </div>
                <div className={`font-editorial text-2xl md:text-3xl mt-2 tabular ${m.accent ? "text-propertic-paper" : "text-propertic-navy"}`}>{m.value}</div>
                <div className={`text-[11px] mt-1.5 leading-snug ${m.accent ? "text-propertic-paper/70" : "text-propertic-navy/55"}`}>{m.sub}</div>
                {m.dynamic && (
                  <div className={`text-[11px] mt-2 pt-2 border-t leading-snug ${m.accent ? "border-white/10 text-propertic-paper/80" : "border-[var(--p-hairline)] text-propertic-navy/70"}`}>
                    {m.dynamic}
                  </div>
                )}
              </div>
            ));
          })()}
        </div>

        {/* ECONOMICS + FINANCING OUTCOME */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <CardShell title={t.econ_title} testId="deal-card-economics">
            <table className="w-full text-sm">
              <tbody className="divide-y divide-[var(--p-hairline)]">
                {[
                  { l: t.econ_price, v: out.acquisition.purchasePrice },
                  // Price-per-sqft: the "1+1" row Rani flagged. Only
                  // shown when the user actually entered a size — if
                  // size is blank we don't fabricate a value.
                  ...(Number(state.sizeSqft) > 0 ? [{
                    l: t.econ_price_sqft,
                    v: out.acquisition.purchasePrice / Number(state.sizeSqft),
                    info: {
                      title: isAR ? "السعر لكل قدم مربع" : "Price per square foot",
                      description: isAR
                        ? "سعر الشراء مقسوماً على المساحة المدخلة. مؤشر سريع للمقارنة بين الوحدات."
                        : "Purchase price divided by the size you entered. A quick benchmark to compare units.",
                      formula: isAR ? "سعر الشراء ÷ المساحة (قدم مربّعة)" : "Purchase Price ÷ Size (sqft)",
                      example: `${fmtMoney(out.acquisition.purchasePrice)} ÷ ${isAR ? fixArThousands(Number(state.sizeSqft).toLocaleString("ar-EG")) : Number(state.sizeSqft).toLocaleString("en-US")} ${isAR ? "قدم²" : "sqft"} = ${fmtMoney(out.acquisition.purchasePrice / Number(state.sizeSqft))}`,
                    },
                  }] : []),
                  { l: t.econ_dld, v: out.acquisition.dldFee },
                  { l: t.econ_broker, v: out.acquisition.brokerFee },
                  { l: t.econ_other, v: out.acquisition.otherPurchaseCosts },
                  { l: t.econ_acq_fees, v: out.acquisition.totalAcquisitionCosts, bold: true },
                  { l: t.econ_acq_total, v: out.acquisition.totalAcquisitionCost, bold: true, info: { title: isAR ? "إجمالي تكلفة التملّك" : "Total acquisition cost", description: isAR ? "سعر الشراء + جميع رسوم التملّك." : "Purchase price plus every acquisition expense.", formula: isAR ? "سعر الشراء + رسوم الدائرة + عمولة الوسيط + رسوم أخرى" : "Price + DLD + Broker + Other", example: isAR ? `${fmtMoney(out.acquisition.purchasePrice)} + ${fmtMoney(out.acquisition.totalAcquisitionCosts)} = ${fmtMoney(out.acquisition.totalAcquisitionCost)}` : `${fmtMoney(out.acquisition.purchasePrice)} + ${fmtMoney(out.acquisition.totalAcquisitionCosts)} = ${fmtMoney(out.acquisition.totalAcquisitionCost)}` } },
                  { l: t.econ_rent, v: engineInputs.annualRent },
                  { l: t.econ_eff_rent, v: out.returns.effectiveAnnualRent, info: { title: isAR ? "الإيجار الفعلي" : "Effective annual rent", description: isAR ? "الإيجار الإجمالي مخصوماً منه الشغور المفترض." : "Gross rent minus any assumed vacancy.", formula: isAR ? "الإيجار السنوي × (١ − نسبة الشغور كنسبة عشرية)" : "Annual Rent × (1 − Vacancy%)", example: isAR ? `${fmtMoney(engineInputs.annualRent)} × (١ − ${fmtPct(engineInputs.vacancyPct)}) = ${fmtMoney(out.returns.effectiveAnnualRent)}` : `${fmtMoney(engineInputs.annualRent)} × (1 − ${fmtPct(engineInputs.vacancyPct)}) = ${fmtMoney(out.returns.effectiveAnnualRent)}` } },
                  { l: t.econ_opex, v: engineInputs.annualOperatingCosts },
                  { l: t.econ_noi, v: out.returns.netOperatingIncome, bold: true, info: { title: isAR ? "صافي الدخل التشغيلي (NOI)" : "Net operating income (NOI)", description: isAR ? "الدخل السنوي بعد الشغور والمصروفات، قبل أقساط الرهن. لا يشمل الفائدة أو الأصل." : "Annual income after vacancy and operating expenses, before mortgage payments. Does NOT include mortgage principal or interest.", formula: isAR ? "الإيجار الفعلي − المصروفات التشغيلية" : "Effective Rent − Operating Expenses", example: `${fmtMoney(out.returns.effectiveAnnualRent)} − ${fmtMoney(engineInputs.annualOperatingCosts)} = ${fmtMoney(out.returns.netOperatingIncome)}` } },
                ].map((r) => (
                  <tr key={r.l}>
                    <td className="py-2 text-propertic-navy/70">
                      {r.l}
                      {r.info && <InfoTip {...r.info} />}
                    </td>
                    <td className={`py-2 tabular text-end ${r.bold ? "font-semibold text-propertic-navy" : "text-propertic-navy/90"}`}>{fmtMoney(r.v)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardShell>

          <CardShell title={t.fin_out_title} testId="deal-card-financing-outcome">
            <table className="w-full text-sm">
              <tbody className="divide-y divide-[var(--p-hairline)]">
                {[
                  { l: t.fin_out_dp, v: out.financing.downPayment },
                  { l: t.fin_out_loan, v: out.financing.mortgageAmount },
                  { l: t.fin_out_monthly, v: out.financing.monthlyPayment },
                  { l: t.fin_out_annual, v: out.financing.annualDebtService, info: { title: isAR ? "خدمة الدين السنوية" : "Annual debt service", description: isAR ? "إجمالي أقساط الرهن المجدولة خلال سنة واحدة. هذه مدفوعات مقرَرة، وليست مصروفاً تشغيلياً." : "Total scheduled mortgage payments over one year. This represents scheduled mortgage payments, not an operating expense.", formula: isAR ? "القسط الشهري × عدد الأقساط المجدولة خلال السنة" : "Monthly Payment × scheduled payments in the year", example: `${fmtMoney(out.financing.monthlyPayment)} × 12 = ${fmtMoney(out.financing.annualDebtService)}` } },
                  { l: t.fin_out_costs, v: out.financing.mortgageCosts },
                  { l: t.fin_out_initial, v: out.financing.initialCashRequired, bold: true, info: { title: isAR ? "النقد المطلوب ابتداءً" : "Initial cash required", description: isAR ? "المبلغ النقدي المطلوب عند بدء المعاملة. يشمل الدفعة الأولى + رسوم التملّك + التكاليف المرتبطة بالرهن (إن وُجدت)." : "The amount of cash required at the start of the transaction. Includes down payment plus acquisition fees plus mortgage related costs.", formula: isCash ? (isAR ? "سعر الشراء + رسوم التملّك" : "Purchase Price + Acquisition Fees") : (isAR ? "الدفعة الأولى + رسوم التملّك + تكاليف الرهن" : "Down Payment + Acq Fees + Mortgage Costs"), example: `${fmtMoney(out.financing.downPayment)} + ${fmtMoney(out.acquisition.totalAcquisitionCosts)} + ${fmtMoney(out.financing.mortgageCosts)} = ${fmtMoney(out.financing.initialCashRequired)}` } },
                ].map((r) => (
                  <tr key={r.l}>
                    <td className="py-2 text-propertic-navy/70">
                      {r.l}
                      {r.info && <InfoTip {...r.info} />}
                    </td>
                    <td className={`py-2 tabular text-end ${r.bold ? "font-semibold text-propertic-navy" : "text-propertic-navy/90"}`}>{fmtMoney(r.v)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardShell>
        </div>

        {/* REQUIRED RENT */}
        <CardShell title={t.req_title} subtitle={t.req_sub} testId="deal-card-required-rent">
          {/* Target yield input lives here — the number the user WANTS drives the reverse calculation directly below. */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 pb-6 border-b hairline">
            <Field
              label={t.field_target_yield}
              hint={t.field_target_yield_hint}
              value={state.targetNetYieldPct}
              onChange={setNum("targetNetYieldPct")}
              step={0.1}
              suffix="%"
              testId="deal-input-target-yield"
              min={0}
              max={50}
              info={{
                title: isAR ? "صافي العائد المستهدف" : "Target net yield",
                description: isAR
                  ? "صافي العائد الذي تريد أن يحقّقه العقار. تستخدمه الأداة لحساب الإيجار المطلوب. لا يوصي المحلّل بمستوى معيّن."
                  : "The annual net yield you want the property to achieve. Used to calculate the rent required to reach that target. The tool never says a particular yield is good or bad.",
                example: isAR
                  ? "مثال: بإدخال ٧٪ يحسب المحلّل الإيجار السنوي اللازم لتحقيق ذلك بعد الشغور والمصروفات."
                  : "Example: enter 7% and the analyzer calculates the annual rent required to produce a 7% net yield after modeled vacancy and operating expenses.",
              }}
            />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.16em] text-propertic-navy/55">{t.req_annual}</div>
              <div className="font-editorial text-2xl mt-1 tabular text-propertic-navy" data-testid="deal-req-annual">{out.requiredRent.notAchievable ? (isAR ? "غير قابل للتحقيق" : "Not achievable") : fmtMoney(out.requiredRent.requiredAnnualRent)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.16em] text-propertic-navy/55">{t.req_monthly}</div>
              <div className="font-editorial text-2xl mt-1 tabular text-propertic-navy" data-testid="deal-req-monthly">{out.requiredRent.notAchievable ? "—" : fmtMoney(out.requiredRent.requiredMonthlyRent)}</div>
              {out.requiredRent.notAchievable && (
                <div className="text-[11px] mt-1 text-red-600 leading-snug" data-testid="deal-req-not-achievable">
                  {isAR ? "عند نسبة شغور ١٠٠٪ لا يوجد إيجار يحقّق العائد المستهدف." : "At 100% vacancy no rent level can reach the target yield."}
                </div>
              )}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.16em] text-propertic-navy/55">{t.req_entered}</div>
              <div className="font-editorial text-2xl mt-1 tabular text-propertic-navy/70" data-testid="deal-req-entered">{fmtMoney(out.requiredRent.enteredAnnualRent)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.16em] text-propertic-navy/55">{t.req_diff}</div>
              <div className={`font-editorial text-2xl mt-1 tabular ${out.requiredRent.differenceAnnualRent > 0 ? "text-red-600" : "text-emerald-700"}`} data-testid="deal-req-diff">
                {out.requiredRent.differenceAnnualRent === null ? "—" : fmtMoney(Math.abs(out.requiredRent.differenceAnnualRent))}
              </div>
              <div className="text-[11px] mt-1 text-propertic-navy/55 leading-snug">
                {out.requiredRent.differenceAnnualRent === null
                  ? " "
                  : out.requiredRent.differenceAnnualRent === 0
                    ? (isAR ? "إيجارك مطابق تماماً للإيجار المطلوب." : "Your entered rent exactly matches the required rent.")
                    : (isAR
                        ? `إيجارك الحالي ${fmtMoney(Math.abs(out.requiredRent.differenceAnnualRent))} ${out.requiredRent.differenceAnnualRent > 0 ? t.req_diff_short : t.req_diff_beats}`
                        : `Your entered annual rent is ${fmtMoney(Math.abs(out.requiredRent.differenceAnnualRent))} ${out.requiredRent.differenceAnnualRent > 0 ? t.req_diff_short : t.req_diff_beats}`)}
              </div>
            </div>
          </div>
        </CardShell>

        {/* CASH VS MORTGAGE */}
        <CardShell title={t.compare_title} subtitle={t.compare_sub} testId="deal-card-compare">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[520px]">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.16em] text-propertic-navy/60">
                  <th className={`py-2 ${isRTL ? "text-end" : "text-start"}`}></th>
                  <th className={`py-2 ${isCash ? "text-propertic-navy" : "text-propertic-navy/40"}`}>{t.compare_cash}</th>
                  <th className={`py-2 ${!isCash ? "text-propertic-navy" : "text-propertic-navy/40"}`}>{t.compare_mortgage}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--p-hairline)]">
                {[
                  { l: t.compare_initial, c: out.comparison.cash.financing.initialCashRequired, m: out.comparison.mortgage.financing.initialCashRequired, k: "money" },
                  { l: t.compare_debt, c: 0, m: out.comparison.mortgage.financing.annualDebtService, k: "money" },
                  { l: t.compare_cf, c: out.comparison.cash.returns.annualCashFlow, m: out.comparison.mortgage.returns.annualCashFlow, k: "money" },
                  { l: t.compare_coc, c: out.comparison.cash.returns.cashOnCashPct, m: out.comparison.mortgage.returns.cashOnCashPct, k: "pct" },
                  { l: t.compare_equity, c: out.comparison.cash.equityAtHorizon, m: out.comparison.mortgage.equityAtHorizon, k: "money", info: { title: isAR ? "حقوق الملكية عند الأفق" : "Equity at horizon", description: isAR ? "قيمة العقار المحاكاة مطروحاً منها الرصيد المتبقّي من الرهن. حقوق الملكية ليست نقداً في يدك ما لم تُحاكَى عملية بيع." : "The modeled property value minus the remaining mortgage balance at the end of the holding period. Equity is NOT the same as cash received unless you model a sale.", formula: isAR ? "قيمة العقار المحاكاة − رصيد القرض المتبقّي" : "Scenario Property Value − Remaining Loan" } },
                  { l: t.compare_cum_cf, c: out.comparison.cash.cumulativeCashFlowAtHorizon, m: out.comparison.mortgage.cumulativeCashFlowAtHorizon, k: "money", info: { title: isAR ? "التدفّق النقدي المتراكم" : "Cumulative cash flow", description: isAR ? "مجموع التدفّقات النقدية السنوية المحاكاة خلال مدّة الاحتفاظ. لا يُعدّ ربحاً محقّقاً." : "Sum of modeled annual cash flows across the holding period. It is NOT realized profit or total return." } },
                ].map((r) => (
                  <tr key={r.l}>
                    <td className="py-2 text-propertic-navy/70">
                      {r.l}
                      {r.info && <InfoTip {...r.info} />}
                    </td>
                    <td className={`py-2 tabular text-end ${isCash ? "text-propertic-navy" : "text-propertic-navy/40"}`}>{r.k === "money" ? fmtMoney(r.c) : fmtPct(r.c)}</td>
                    <td className={`py-2 tabular text-end ${!isCash ? "text-propertic-navy" : "text-propertic-navy/40"}`}>{r.k === "money" ? fmtMoney(r.m) : fmtPct(r.m)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-[11px] text-propertic-navy/55 mt-3">
            {isAR ? `مدّة الاحتفاظ: ${arCount(out.comparison.horizonYears, { one: "سنة واحدة", two: "سنتان", few: "سنوات", many: "سنة" })}` : `Horizon: ${out.comparison.horizonYears} ${out.comparison.horizonYears === 1 ? "year" : "years"}`}
          </div>
          <div className="mt-4 pt-4 border-t hairline">
            <div className="text-[10px] uppercase tracking-[0.16em] text-propertic-navy/55 font-semibold mb-2">
              {isAR ? "لماذا يختلفان؟" : "Why does this differ?"}
            </div>
            <p className="text-[12.5px] text-propertic-navy/70 leading-relaxed">
              {isAR
                ? "يمكن للرهن أن يقلّل النقد المطلوب عند الشراء، لكنه في الوقت نفسه قد يقلّل التدفّق النقدي السنوي أو يجعله سلبياً. تعرض الأداة السيناريوهين حتى تقارن أثر التمويل بنفسك. لا يوصي المحلّل بأيّ من الخيارَين."
                : "A mortgage can reduce the amount of cash required upfront, but the mortgage payments can also reduce or eliminate annual cash flow. The analyzer shows both scenarios so you can compare the effect of leverage. It never recommends one over the other."}
            </p>
          </div>
        </CardShell>

        {/* YEAR-BY-YEAR PROJECTION */}
        <CardShell title={t.projection_title} subtitle={scenarioOn ? t.projection_sub_on : t.projection_sub_off} testId="deal-card-projection">
          <div className="mb-3 flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-900 rounded-lg px-3 py-2 text-[12px] leading-snug">
            <Info className="w-3.5 h-3.5 flex-shrink-0" />
            <span>
              {isAR
                ? "سيناريو توضيحي فقط. النتائج محسوبة من افتراضاتك أنت، وليست توقّعاً لأسعار العقار أو الإيجار أو المصروفات أو الأداء الاستثماري."
                : "Illustrative scenario. Results are calculated from the assumptions you entered, not a forecast of property prices, rents, expenses or investment performance."}
            </span>
            <InfoTip
              title={isAR ? "كيف تقرأ هذا الجدول" : "How to read this table"}
              description={isAR
                ? "كل صفّ يمثّل سنة واحدة من السيناريو. قيمة السيناريو = القيمة المحاكاة للعقار حسب افتراضك للنمو. NOI = الدخل بعد الشغور والمصروفات وقبل التمويل. التدفّق النقدي = NOI ناقص خدمة الدين. حقوق الملكية = القيمة المحاكاة ناقص الرصيد المتبقّي."
                : "Each row represents one year of your scenario. Scenario value uses your appreciation assumption. Annual rent/opex use your growth assumptions. NOI = income after vacancy & opex, before financing. Cash flow = NOI − debt service. Equity = scenario value − remaining loan balance."}
            />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs tabular min-w-[720px]">
              <thead>
                <tr className="text-propertic-navy/55 uppercase tracking-[0.12em] text-[10px]">
                  <th className={`py-2 ${isRTL ? "text-end" : "text-start"}`}>{t.y_year}</th>
                  <th className="py-2 text-end">{t.y_val}</th>
                  <th className="py-2 text-end">{t.y_rent}</th>
                  <th className="py-2 text-end">{t.y_opex}</th>
                  <th className="py-2 text-end">{t.y_noi}</th>
                  <th className="py-2 text-end">{t.y_debt}</th>
                  <th className="py-2 text-end">{t.y_cf}</th>
                  <th className="py-2 text-end">{t.y_cumcf}</th>
                  <th className="py-2 text-end">{t.y_loan}</th>
                  <th className="py-2 text-end">{t.y_equity}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--p-hairline)]">
                {out.projection.map((r) => (
                  <tr key={r.year}>
                    <td className={`py-2 text-propertic-gold-2 font-semibold ${isRTL ? "text-end" : "text-start"}`}>{isAR ? arDigits(r.year) : r.year}</td>
                    <td className="py-2 text-end">{fmtInt(toDisp(r.scenarioPropertyValue))}</td>
                    <td className="py-2 text-end">{fmtInt(toDisp(r.annualRent))}</td>
                    <td className="py-2 text-end">{fmtInt(toDisp(r.annualOperatingCosts))}</td>
                    <td className="py-2 text-end">{fmtInt(toDisp(r.netOperatingIncome))}</td>
                    <td className="py-2 text-end">{fmtInt(toDisp(r.annualDebtService))}</td>
                    <td className={`py-2 text-end ${r.annualCashFlow >= 0 ? "text-emerald-700" : "text-red-600"}`}>{fmtInt(toDisp(r.annualCashFlow))}</td>
                    <td className="py-2 text-end">{fmtInt(toDisp(r.cumulativeCashFlow))}</td>
                    <td className="py-2 text-end">{fmtInt(toDisp(r.remainingMortgage))}</td>
                    <td className="py-2 text-end text-propertic-navy/85 font-semibold">{fmtInt(toDisp(r.equity))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-propertic-navy/55 mt-3 leading-relaxed">
            {t.disclaimer}
          </p>
        </CardShell>

        {/* HOW CALCULATIONS WORK — expandable, kept out of the way on mobile */}
        <div className="bg-white border hairline rounded-2xl p-5 md:p-6" data-testid="deal-card-formulas">
          <button
            type="button"
            onClick={() => setShowFormulas((v) => !v)}
            className="w-full flex items-center justify-between gap-4"
            aria-expanded={showFormulas}
            data-testid="deal-formulas-toggle"
          >
            <span className="text-[10px] uppercase tracking-[0.18em] text-propertic-navy/60 font-semibold">
              {isAR ? "كيف تُحسب الأرقام" : "How calculations work"}
            </span>
            <ChevronDown className={`w-4 h-4 text-propertic-navy/60 transition-transform flex-shrink-0 ${showFormulas ? "rotate-180" : ""}`} />
          </button>
          {showFormulas && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-[12.5px]">
              {[
                [isAR ? "الإيجار الفعلي" : "Effective rent", isAR ? "الإيجار السنوي × (١ − نسبة الشغور كنسبة عشرية)" : "Annual Rent × (1 − Vacancy%)"],
                [isAR ? "صافي الدخل التشغيلي (NOI)" : "Net operating income (NOI)", isAR ? "الإيجار الفعلي − المصروفات التشغيلية" : "Effective Rent − Operating Expenses"],
                [isAR ? "صافي العائد" : "Net yield", isAR ? "صافي الدخل التشغيلي ÷ إجمالي تكلفة التملّك × ١٠٠" : "NOI ÷ Total Acquisition Cost × 100"],
                [isAR ? "خدمة الدين السنوية" : "Annual debt service", isAR ? "القسط الشهري × عدد الأقساط المجدولة خلال السنة" : "Monthly Mortgage Payment × scheduled payments in the year"],
                [isAR ? "التدفّق النقدي السنوي" : "Annual cash flow", isAR ? "صافي الدخل التشغيلي − خدمة الدين السنوية" : "NOI − Annual Debt Service"],
                [isAR ? "العائد النقدي" : "Cash on cash", isAR ? "التدفّق النقدي السنوي ÷ النقد المستثمر عند الشراء × ١٠٠" : "Annual Cash Flow ÷ Initial Cash Required × 100"],
                [isAR ? "حقوق الملكية" : "Equity", isAR ? "قيمة العقار المحاكاة − رصيد القرض المتبقّي" : "Scenario Property Value − Remaining Loan"],
                [isAR ? "إيجار التعادل النقدي" : "Cash flow break even rent", isAR ? "(المصروفات التشغيلية + خدمة الدين) ÷ (١ − نسبة الشغور)" : "(Opex + Debt Service) ÷ (1 − Vacancy%)"],
              ].map(([name, formula]) => (
                <div key={name} className="flex flex-col gap-1 border hairline rounded-lg p-3 bg-propertic-sand/40">
                  <div className="text-[11px] uppercase tracking-[0.14em] text-propertic-navy/60 font-semibold">{name}</div>
                  <div className="font-mono text-[11.5px] text-propertic-navy tabular">{formula}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* CONSULTATION CTA */}
        <section className="mt-4">
          <div className="text-center max-w-2xl mx-auto mb-6">
            <h2 className="font-editorial text-3xl md:text-4xl text-propertic-navy">{t.cta_title}</h2>
            <p className="text-propertic-navy/70 mt-3 text-[15px] leading-relaxed">{t.cta_sub}</p>
          </div>
          <ConsultationForm />
        </section>

        {/* FAQ */}
        <CardShell title={t.faq_title} testId="deal-card-faq">
          <div className="divide-y divide-[var(--p-hairline)]">
            {faqList.map((f, idx) => {
              const open = openFaq === idx;
              return (
                <div key={idx} className="py-1">
                  <button
                    onClick={() => setOpenFaq(open ? null : idx)}
                    className="w-full flex items-center justify-between gap-4 py-3 text-start"
                    aria-expanded={open}
                    data-testid={`deal-faq-q-${idx}`}
                  >
                    <span className="text-sm font-medium text-propertic-navy">{f.q}</span>
                    <ChevronDown className={`w-4 h-4 text-propertic-navy/60 transition-transform flex-shrink-0 ${open ? "rotate-180" : ""}`} />
                  </button>
                  {/* Always in DOM so FAQPage schema stays valid; simple show/hide */}
                  <div className={`text-sm text-propertic-navy/75 leading-relaxed pb-4 ${open ? "block" : "hidden"}`}>
                    {f.a}
                  </div>
                </div>
              );
            })}
          </div>
        </CardShell>

        <p className="text-[11px] text-propertic-navy/55 leading-relaxed text-center max-w-3xl mx-auto pt-4">
          {t.disclaimer}
        </p>
      </div>
    </div>
  );
}
